import { Injectable, signal } from '@angular/core';

type ToastTone = 'success' | 'error' | 'warning' | 'info';
type ToastAriaLive = 'polite' | 'assertive';

interface ToastQueueItem {
  id: string | number;
  title?: string;
  message: string;
  tone?: ToastTone;
  timeoutMs?: number | null;
  origin?: 'top' | 'bottom' | 'left' | 'right';
  animationDurationMs?: number;
  dismissible?: boolean;
  ariaLive?: ToastAriaLive;
}

interface ShowToastInput {
  title: string;
  message: string;
  tone?: ToastTone;
  ariaLive?: ToastAriaLive;
  timeoutMs?: number | null;
  dismissible?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly toasts = signal<ToastQueueItem[]>([]);

  private nextToastId = 1;

  show(input: ShowToastInput): void {
    const tone = input.tone ?? 'info';
    const ariaLive = input.ariaLive ?? (tone === 'error' ? 'assertive' : 'polite');

    const toast: ToastQueueItem = {
      id: this.nextToastId++,
      title: input.title,
      message: input.message,
      tone,
      ariaLive,
      timeoutMs: input.timeoutMs ?? 5000,
      origin: 'right',
      animationDurationMs: 500,
      dismissible: input.dismissible ?? true
    };

    this.toasts.update((items) => [...items, toast]);
  }

  showSuccess(title: string, message: string): void {
    this.show({ title, message, tone: 'success', ariaLive: 'polite' });
  }

  showError(title: string, message: string): void {
    this.show({ title, message, tone: 'error', ariaLive: 'assertive' });
  }

  hide(id: string | number): void {
    this.toasts.update((items) => items.filter((item) => item.id !== id));
  }

  onToastsChange(toasts: ToastQueueItem[]): void {
    this.toasts.set(toasts);
  }
}